import React, { useState, useMemo } from 'react';
import ProductCard from '../components/ProductCard';
import { simCards } from '../data/simCards';
import './Products.css';

const Products: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const regions = Array.from(new Set(simCards.map(card => card.region)));

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = simCards.filter(card => {
      const matchesSearch = card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           card.country.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = !selectedRegion || card.region === selectedRegion;
      
      let matchesPrice = true;
      if (priceRange) {
        const price = card.price;
        switch (priceRange) {
          case 'under-30':
            matchesPrice = price < 30;
            break;
          case '30-50':
            matchesPrice = price >= 30 && price <= 50;
            break;
          case 'over-50':
            matchesPrice = price > 50;
            break;
        }
      }
      
      return matchesSearch && matchesRegion && matchesPrice;
    });

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [searchTerm, selectedRegion, priceRange, sortBy]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRegion('');
    setPriceRange('');
    setSortBy('name');
  };

  return (
    <div className="products-page">
      <div className="container">
        <div className="page-header">
          <h1>International SIM Cards</h1>
          <p>Choose from our wide selection of premium SIM cards for global connectivity</p>
        </div>

        <div className="filters-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by country or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filters">
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="filter-select"
            >
              <option value="">All Regions</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>

            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e.target.value)}
              className="filter-select"
            >
              <option value="">All Prices</option>
              <option value="under-30">Under $30</option>
              <option value="30-50">$30 - $50</option>
              <option value="over-50">Over $50</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name">Sort by Name</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>

            <button onClick={clearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        </div>

        <div className="results-info">
          <p>Showing {filteredAndSortedProducts.length} of {simCards.length} products</p>
        </div>

        <div className="products-grid">
          {filteredAndSortedProducts.map(simCard => (
            <ProductCard key={simCard.id} simCard={simCard} />
          ))}
        </div>

        {filteredAndSortedProducts.length === 0 && (
          <div className="no-results">
            <h3>No products found</h3>
            <p>Try adjusting your search criteria or clearing the filters.</p>
            <button onClick={clearFilters} className="cta-button primary">
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
